import { inject } from '@angular/core';
import { CanActivateChildFn, CanActivateFn, Router, RouterStateSnapshot } from '@angular/router';

import { AuthService } from '../services/auth.service';
import { MobileLayoutService } from '../services/mobile-layout.service';
import {
  ONBOARDING_ANGULAR_DEBUG_MODE,
  OnboardingDraftService,
} from '../services/onboarding-draft.service';

function requireAuthenticated(state: RouterStateSnapshot): true | ReturnType<Router['createUrlTree']> {
  const auth = inject(AuthService);

  if (auth.isAuthenticated()) {
    return true;
  }

  const router = inject(Router);
  return router.createUrlTree(['/login'], {
    queryParams: {
      returnUrl: state.url,
    },
  });
}

export const authRequiredGuard: CanActivateFn = (_route, state) => requireAuthenticated(state);

export const authRequiredChildGuard: CanActivateChildFn = (_childRoute, state) => requireAuthenticated(state);

export const firstLaunchOnboardingGuard: CanActivateFn = async (_route, state) => {
  // El recorrido inicial es para visitantes. Una sesión existente puede
  // conservar un borrador de bienvenida sin tener que repetir el onboarding.
  if (inject(AuthService).isAuthenticated()) return true;

  const drafts = inject(OnboardingDraftService);
  const mobileLayout = inject(MobileLayoutService);
  const isAngularDebugMode = inject(ONBOARDING_ANGULAR_DEBUG_MODE);
  const router = inject(Router);
  const isNative = drafts.isNative();
  const allowDebugWeb = !isNative && isAngularDebugMode() && mobileLayout.isHandset();
  if (!isNative && !allowDebugWeb) return true;
  const [shouldEnterOnFirstLaunch, hasActiveDraft] = await Promise.all([
    drafts.shouldEnterOnFirstLaunch(allowDebugWeb),
    drafts.hasActiveDraft(),
  ]);
  if (!shouldEnterOnFirstLaunch && !hasActiveDraft) return true;
  return router.createUrlTree(['/onboarding'], {
    queryParams: {
      returnUrl: state.url || '/home',
      source: allowDebugWeb ? 'debug_mobile_web' : 'first_launch',
    },
  });
};

export const onboardingPromptChildGuard: CanActivateChildFn = (_childRoute, state) => {
  const auth = inject(AuthService);
  const currentUser = auth.currentUser();

  if (!currentUser?.should_show_onboarding) {
    return true;
  }

  const router = inject(Router);
  return router.createUrlTree(['/onboarding'], {
    queryParams: {
      returnUrl: state.url || '/home',
      source: 'route_guard',
    },
  });
};
